import { config } from '@/config';
import { Context } from '@/context/appContext';
import { fireSignupConversion, logEvent } from '@/helpers/eventLogger';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Button, CircularProgress, SvgIcon } from '@mui/material';
import { googleAuth } from '@tailored-pdf/api';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

const GoogleIcon = (
  <SvgIcon>
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_2005_1368)">
        <path
          d="M24.2663 12.2764C24.2663 11.4607 24.2001 10.6406 24.059 9.83807H12.7402V14.4591H19.222C18.953 15.9494 18.0888 17.2678 16.8233 18.1056V21.1039H20.6903C22.9611 19.0139 24.2663 15.9274 24.2663 12.2764Z"
          fill="#4285F4"
        />
        <path
          d="M12.74 24.0008C15.9764 24.0008 18.7058 22.9382 20.6944 21.1039L16.8274 18.1055C15.7516 18.8375 14.3626 19.252 12.7444 19.252C9.61376 19.252 6.95934 17.1399 6.00693 14.3003H2.01648V17.3912C4.05359 21.4434 8.20278 24.0008 12.74 24.0008Z"
          fill="#34A853"
        />
        <path
          d="M6.00277 14.3003C5.50011 12.8099 5.50011 11.1961 6.00277 9.70575V6.61481H2.01674C0.314734 10.0056 0.314734 14.0004 2.01674 17.3912L6.00277 14.3003Z"
          fill="#FBBC04"
        />
        <path
          d="M12.74 4.74966C14.4508 4.7232 16.1043 5.36697 17.3433 6.54867L20.7694 3.12262C18.6 1.0855 15.7207 -0.034466 12.74 0.000808666C8.20277 0.000808666 4.05359 2.55822 2.01648 6.61481L6.00252 9.70575C6.95052 6.86173 9.60935 4.74966 12.74 4.74966Z"
          fill="#EA4335"
        />
      </g>
      <defs>
        <clipPath id="clip0_2005_1368">
          <rect width="24" height="24" fill="white" transform="translate(0.5)" />
        </clipPath>
      </defs>
    </svg>
  </SvgIcon>
);

function GoogleButton() {
  const { t } = useTranslation('web', { keyPrefix: 'components.AuthModal' });
  const { t: tCommon } = useTranslation('web', { keyPrefix: 'common' });
  const {
    dispatch,
    state: { authState },
  } = useContext(Context);

  const { notifyError } = useNotifications();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(() => {
    // The Google Identity Services script (gsi/client) loads asynchronously, so `google`
    // may not exist yet if the user clicks before it finishes loading or it was blocked.
    if (typeof google === 'undefined' || !google.accounts?.oauth2) {
      logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_IN_ERROR', trace: 'google identity services not loaded' });
      notifyError(tCommon('error'));
      return;
    }

    logEvent({ logType: 'EVENT', eventType: 'USER_SIGN_IN' });
    if (authState.state === 'SIGN_UP') {
      fireSignupConversion();
    }

    const client = google.accounts.oauth2.initCodeClient({
      client_id: config.AUTH_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid',
      ux_mode: 'popup',

      callback: async (response) => {
        if (response.code && authState.state) {
          setLoading(true);
          try {
            const resp = await googleAuth({ code: response.code });
            dispatch({
              type: AppStateActions.SET_AUTH_TOKEN,
              payload: { token: resp.data, source: authState.state },
            });
            dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: undefined } });
            setLoading(false);
          } catch (e) {
            logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_IN_ERROR', trace: e });
            setLoading(false);
            notifyError(tCommon('error'));
          }
        } else {
          setLoading(false);
        }
      },
      error_callback: (_ignore) => {
        //probably user declined auth
        setLoading(false);
      },
    });

    client.requestCode();
  }, [authState.state, dispatch, notifyError, tCommon]);

  return (
    <Button
      variant="outlined"
      className="buttonSecondary"
      startIcon={loading ? <CircularProgress size={20} /> : GoogleIcon}
      onClick={handleClick}
      disabled={loading}
    >
      {t('continueWithGoogle')}
    </Button>
  );
}

export { GoogleButton };
